import { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import {
  Activity,
  AlertTriangle,
  Bot,
  CheckCircle2,
  Clock,
  Database,
  Download,
  FileSearch,
  RefreshCw,
  ShieldAlert,
  Wallet,
  Webhook,
  Zap,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const PERIODS = [
  { id: 'day', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week', label: '7 Days' },
  { id: 'month', label: '30 Days' },
  { id: '90d', label: '90 Days' },
  { id: 'all_time', label: 'All Time' },
];

const tooltipStyle = {
  background: '#0B0E14',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 10,
  color: '#fff',
  fontSize: 12,
};

const fmtUSD = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtInt = (v) => Number(v || 0).toLocaleString();
const val = (obj, key, fallback = 0) => obj?.[key] ?? fallback;

export default function OperationsCenterPage() {
  const [period, setPeriod] = useState('day');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get(`/api/admin/analytics/operations-center?period=${period}`);
      setData(res.data || {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [period]);
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [autoRefresh, period]);

  const financeChart = useMemo(() => {
    const f = data?.finance || {};
    return [
      { name: 'Deposits', amount: val(f.deposits, 'amount'), count: val(f.deposits, 'count') },
      { name: 'Withdrawals', amount: val(f.withdrawals, 'amount'), count: val(f.withdrawals, 'count') },
      { name: 'Pending In', amount: val(f.pending_deposits, 'amount'), count: val(f.pending_deposits, 'count') },
      { name: 'Pending Out', amount: val(f.pending_withdrawals, 'amount'), count: val(f.pending_withdrawals, 'count') },
      { name: 'Failed In', amount: val(f.failed_deposits, 'amount'), count: val(f.failed_deposits, 'count') },
      { name: 'Failed Out', amount: val(f.failed_withdrawals, 'amount'), count: val(f.failed_withdrawals, 'count') },
    ];
  }, [data]);

  const exportSnapshot = () => {
    const rows = [
      ['Metric', 'Value'],
      ['Deposits', data?.finance?.deposits?.amount || 0],
      ['Withdrawals', data?.finance?.withdrawals?.amount || 0],
      ['Net Profit', data?.finance?.net_profit || 0],
      ['Pending Deposits', data?.finance?.pending_deposits?.amount || 0],
      ['Pending Withdrawals', data?.finance?.pending_withdrawals?.amount || 0],
      ['Wallet Cash Total', data?.reconciliation?.wallet_cash_total || 0],
      ['Wallet FP Total', data?.reconciliation?.wallet_fp_total || 0],
      ['Negative Wallets', data?.reconciliation?.negative_wallets || 0],
      ['Ledger Mismatches', data?.reconciliation?.ledger_mismatches?.length || 0],
      ['Provider Problems', data?.providers?.problem_count || 0],
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `teamlobby-operations-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (loading && !data) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  const f = data?.finance || {};
  const r = data?.reconciliation || {};
  const risk = data?.risk?.signals || [];
  const integrity = data?.integrity?.checks || [];
  const providerGames = data?.providers?.games || [];
  const gateways = data?.providers?.payment_gateways || [];
  const requests = data?.manual_approvals?.recent_requests || [];
  const audit = data?.audit?.recent || [];
  const webhooks = data?.webhooks?.rollups || [];
  const backup = data?.backup?.status || {};

  return (
    <div className="space-y-6" data-testid="operations-center-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Zap className="w-6 h-6 text-[#D4AF37]" /> Operations Center
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Real operational control: finance ledger, reconciliation, providers, approvals, audit, webhooks, backups, and risk checks.
          </p>
          <p className="text-[11px] text-gray-500 mt-1">Generated: {data?.generated_at ? new Date(data.generated_at).toLocaleString() : '—'}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-xl bg-[#0B0E14] border border-white/10 p-1">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${period === p.id ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button onClick={() => setAutoRefresh((v) => !v)} className={`px-3 py-2 rounded-lg border text-xs font-semibold ${autoRefresh ? 'border-green-500/30 text-green-400 bg-green-500/10' : 'border-white/10 text-gray-400'}`}>
            {autoRefresh ? 'Live 30s' : 'Paused'}
          </button>
          <button onClick={exportSnapshot} className="px-3 py-2 rounded-lg border border-white/10 text-gray-300 hover:text-white text-xs flex items-center gap-2">
            <Download className="w-4 h-4" /> Export
          </button>
          <button onClick={() => load()} className="p-2 rounded-lg border border-white/10 text-gray-400 hover:text-white">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-6 gap-4">
        <Kpi icon={Wallet} label="Total In" value={fmtUSD(val(f.deposits, 'amount'))} sub={`${fmtInt(val(f.deposits, 'count'))} approved`} color="#22c55e" />
        <Kpi icon={Wallet} label="Total Out" value={fmtUSD(val(f.withdrawals, 'amount'))} sub={`${fmtInt(val(f.withdrawals, 'count'))} approved`} color="#ef4444" />
        <Kpi icon={Activity} label="Profit" value={fmtUSD(f.net_profit)} sub="approved in - approved out" color="#D4AF37" />
        <Kpi icon={Clock} label="Pending In" value={fmtUSD(val(f.pending_deposits, 'amount'))} sub={`${fmtInt(val(f.pending_deposits, 'count'))} requests`} color="#f59e0b" />
        <Kpi icon={Clock} label="Pending Out" value={fmtUSD(val(f.pending_withdrawals, 'amount'))} sub={`${fmtInt(val(f.pending_withdrawals, 'count'))} requests`} color="#fb7185" />
        <Kpi icon={ShieldAlert} label="Risk Signals" value={fmtInt(risk.filter((x) => x.severity !== 'ok').length)} sub="non-ok signals" color="#f97316" />
      </div>

      <div className="grid grid-cols-1 2xl:grid-cols-3 gap-4">
        <Panel title="Finance Flow" icon={Activity} className="2xl:col-span-2">
          <ResponsiveContainer width="100%" height={270}>
            <BarChart data={financeChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
              <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v, name) => name === 'amount' ? fmtUSD(v) : fmtInt(v)} />
              <Bar dataKey="amount" fill="#D4AF37" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Wallet Reconciliation" icon={Database}>
          <div className="grid grid-cols-2 gap-3">
            <Mini label="Wallets" value={fmtInt(r.wallet_count)} />
            <Mini label="Cash Total" value={fmtUSD(r.wallet_cash_total)} />
            <Mini label="FP Total" value={`${fmtInt(r.wallet_fp_total)} FP`} />
            <Mini label="Pending Cash" value={fmtUSD(r.pending_cash)} />
            <Mini label="Negative Wallets" value={fmtInt(r.negative_wallets)} danger={r.negative_wallets > 0} />
            <Mini label="Ledger Mismatch" value={fmtInt(r.ledger_mismatches?.length)} danger={(r.ledger_mismatches?.length || 0) > 0} />
          </div>
        </Panel>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Panel title="Risk & Fraud Signals" icon={ShieldAlert}>
          <div className="space-y-2">
            {risk.map((item) => <Signal key={item.label} item={item} />)}
          </div>
        </Panel>
        <Panel title="Data Integrity Checks" icon={CheckCircle2}>
          <div className="space-y-2">
            {integrity.map((item) => <CheckRow key={item.check} item={item} />)}
          </div>
        </Panel>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Panel title="Provider Command Center" icon={Activity}>
          <Table
            columns={['Provider', 'Games', 'Accounts', 'Problems', 'Last Error']}
            rows={providerGames.slice(0, 12).map((p) => [
              p.provider_code,
              p.enabled_games,
              p.accounts,
              <span className={p.problem_accounts ? 'text-red-400 font-bold' : 'text-green-400'}>{p.problem_accounts}</span>,
              p.last_error || '—',
            ])}
            empty="No game provider records"
          />
        </Panel>
        <Panel title="Payment Gateway Health" icon={Webhook}>
          <Table
            columns={['Gateway', 'Enabled', 'Status', 'Last Check', 'Error']}
            rows={gateways.map((g) => [
              g.display_name || g.provider_code,
              g.enabled ? 'Yes' : 'No',
              g.health_status || 'unchecked',
              g.last_checked_at ? new Date(g.last_checked_at).toLocaleString() : '—',
              g.last_error || '—',
            ])}
            empty="No payment gateways configured"
          />
        </Panel>
      </div>

      <Panel title="Manual Approval & Telegram Audit" icon={Bot}>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2">
            <Table
              columns={['Created', 'Player', 'Type', 'Amount', 'Status', 'Proof']}
              rows={requests.slice(0, 12).map((req) => [
                req.created_at ? new Date(req.created_at).toLocaleString() : '—',
                req.username || req.user_id || '—',
                req.type,
                fmtUSD(req.amount_usd ?? req.amount),
                req.status,
                req.proof_image_url ? 'Uploaded' : 'Missing',
              ])}
              empty="No recent manual requests"
            />
          </div>
          <div className="space-y-2">
            {(data?.manual_approvals?.telegram_rollup || []).map((bot) => (
              <Mini key={bot._id || 'unknown'} label={bot._id || 'unknown bot'} value={`${fmtInt(bot.ok)} ok / ${fmtInt(bot.failed)} failed`} />
            ))}
          </div>
        </div>
      </Panel>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Panel title="Admin Audit & Settings History" icon={FileSearch}>
          <Table
            columns={['Time', 'Actor', 'Action', 'Target']}
            rows={audit.slice(0, 12).map((a) => [
              a.created_at ? new Date(a.created_at).toLocaleString() : '—',
              a.admin_username || a.actor_id || a.actor_type || 'system',
              a.action || '—',
              `${a.target_type || '—'} ${a.target_id || ''}`,
            ])}
            empty="No audit records"
          />
        </Panel>
        <Panel title="Webhook Monitor & Backup" icon={Webhook}>
          <div className="space-y-4">
            <Table
              columns={['Source', 'Count', 'Failed', 'Last Status']}
              rows={webhooks.map((w) => [w.collection, w.count, w.failed, w.last?.status || String(w.last?.ok ?? '—')])}
              empty="No webhook activity in this window"
            />
            <div className="rounded-xl border border-white/10 bg-[#0B0E14] p-3">
              <p className="text-xs uppercase tracking-[0.16em] text-[#D4AF37] font-semibold">Backup Status</p>
              <p className="text-sm text-white mt-2">{backup.last_status || backup.status || 'Unknown'}</p>
              <p className="text-xs text-gray-500 mt-1">Last run: {backup.last_run_at ? new Date(backup.last_run_at).toLocaleString() : '—'}</p>
              {backup.last_error && <p className="text-xs text-red-400 mt-1">{backup.last_error}</p>}
            </div>
          </div>
        </Panel>
      </div>

      <Panel title="Recent Finance Ledger" icon={Wallet}>
        <Table
          columns={['Time', 'User', 'Reason', 'Type', 'Amount', 'Before', 'After']}
          rows={(f.recent_ledger || []).slice(0, 20).map((row) => [
            row.created_at ? new Date(row.created_at).toLocaleString() : '—',
            row.user_id || '—',
            row.reason || '—',
            row.balance_type === 'PLAY_CREDITS' ? 'FP' : 'Cash',
            `${row.direction === 'DEBIT' ? '-' : '+'}${row.balance_type === 'PLAY_CREDITS' ? fmtInt(row.amount) + ' FP' : fmtUSD(row.amount)}`,
            row.before_balance ?? '—',
            row.after_balance ?? '—',
          ])}
          empty="No ledger rows"
        />
      </Panel>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#131822] p-4">
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}1f`, color }}>
          <Icon className="w-4 h-4" />
        </span>
        {label}
      </div>
      <p className="text-2xl font-bold mt-3" style={{ color }}>{value}</p>
      <p className="text-[11px] text-gray-500 mt-1">{sub}</p>
    </div>
  );
}

function Panel({ title, icon: Icon, children, className = '' }) {
  return (
    <section className={`rounded-2xl border border-white/10 bg-[#131822] p-5 ${className}`}>
      <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#D4AF37]" /> {title}
      </h3>
      {children}
    </section>
  );
}

function Mini({ label, value, danger = false }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0B0E14] p-3">
      <p className="text-[10px] uppercase tracking-[0.14em] text-gray-500">{label}</p>
      <p className={`text-lg font-bold mt-1 ${danger ? 'text-red-400' : 'text-white'}`}>{value}</p>
    </div>
  );
}

function Signal({ item }) {
  const bad = item.severity !== 'ok';
  return (
    <div className="flex items-center justify-between rounded-xl border border-white/10 bg-[#0B0E14] p-3">
      <div className="flex items-center gap-2">
        {bad ? <AlertTriangle className="w-4 h-4 text-red-400" /> : <CheckCircle2 className="w-4 h-4 text-green-400" />}
        <span className="text-sm text-gray-200">{item.label}</span>
      </div>
      <span className={`font-bold ${bad ? 'text-red-400' : 'text-green-400'}`}>{fmtInt(item.value)}</span>
    </div>
  );
}

function CheckRow({ item }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-white/10 bg-[#0B0E14] p-3">
      <div className="flex items-center gap-2">
        {item.ok ? <CheckCircle2 className="w-4 h-4 text-green-400" /> : <AlertTriangle className="w-4 h-4 text-red-400" />}
        <span className="text-sm text-gray-200">{item.check}</span>
      </div>
      <span className={item.ok ? 'text-green-400' : 'text-red-400'}>{item.ok ? 'OK' : fmtInt(item.count)}</span>
    </div>
  );
}

function Table({ columns, rows, empty }) {
  if (!rows?.length) return <p className="text-sm text-gray-500 py-6 text-center">{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-white/10">
            {columns.map((c) => <th key={c} className="text-left py-2 px-2">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
              {row.map((cell, i) => <td key={i} className="py-2 px-2 text-gray-300 max-w-[260px] truncate">{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
